import { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { initGA, trackPageView } from "@/lib/analytics";
import { adoptFromUrl } from "@/lib/pmf";
import AuthCallback from "@/components/AuthCallback";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SplashScreen } from "@/components/SplashScreen";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Quiz from "@/pages/Quiz";
import Subscribe from "@/pages/Subscribe";
import Account from "@/pages/Account";
import Dashboard from "@/pages/Dashboard";
import Lesson from "@/pages/Lesson";
import WhatsNew from "@/pages/WhatsNew";
import ToolsCatalogue from "@/pages/ToolsCatalogue";
import ToolCourse from "@/pages/ToolCourse";
import AIUniverse from "@/pages/AIUniverse";
import Explore from "@/pages/Explore";
import Me from "@/pages/Me";
import PublicPaths from "@/pages/PublicPaths";
import PublicPath from "@/pages/PublicPath";
import PublicTools from "@/pages/PublicTools";
import PublicTool from "@/pages/PublicTool";
import About from "@/pages/About";
import MindMap from "@/pages/MindMap";
import Timeline from "@/pages/Timeline";
import SharedRoadmap from "@/pages/SharedRoadmap";
import Admin from "@/pages/Admin";
import Pricing from "@/pages/Pricing";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Compliance from "@/pages/Compliance";
import PromptLibrary from "@/pages/PromptLibrary";
import Analytics from "@/pages/Analytics";
import Survey from "@/pages/Survey";
import SurveyResults from "@/pages/SurveyResults";

function App() {
  const location = useLocation();

  // Google Analytics: init once, then log a pageview on every SPA route change.
  useEffect(() => { initGA(); adoptFromUrl(); }, []);
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Handle Google OAuth return BEFORE any routing / protected checks.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
    <SplashScreen />
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/paths" element={<PublicPaths />} />
      <Route path="/paths/:slug" element={<PublicPath />} />
      <Route path="/ai-tools" element={<PublicTools />} />
      <Route path="/ai-tools/:slug" element={<PublicTool />} />
      <Route path="/about" element={<About />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/youtube-compliance" element={<Compliance />} />
      <Route path="/survey" element={<Survey />} />
      <Route path="/r/:token" element={<SharedRoadmap />} />
      <Route
        path="/admin/results"
        element={
          <ProtectedRoute adminOnly>
            <SurveyResults />
          </ProtectedRoute>
        }
      />
      <Route path="/quiz" element={<Quiz />} />
      <Route
        path="/subscribe"
        element={
          <ProtectedRoute>
            <Subscribe />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireSub>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lesson/:videoId"
        element={
          <ProtectedRoute requireSub>
            <Lesson />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whats-new"
        element={
          <ProtectedRoute requireSub>
            <WhatsNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools"
        element={
          <ProtectedRoute requireSub>
            <ToolsCatalogue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/course"
        element={
          <ProtectedRoute requireSub>
            <ToolCourse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/:slug"
        element={
          <ProtectedRoute requireSub>
            <ToolCourse />
          </ProtectedRoute>
        }
      />
      <Route
        path="/universe"
        element={
          <ProtectedRoute requireSub>
            <Explore />
          </ProtectedRoute>
        }
      />
      <Route
        path="/explore"
        element={
          <ProtectedRoute requireSub>
            <Explore />
          </ProtectedRoute>
        }
      />
      <Route
        path="/me"
        element={
          <ProtectedRoute>
            <Me />
          </ProtectedRoute>
        }
      />
      <Route
        path="/universe/mindmap"
        element={
          <ProtectedRoute requireSub>
            <MindMap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/universe/timeline"
        element={
          <ProtectedRoute requireSub>
            <Timeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prompts"
        element={
          <ProtectedRoute requireSub>
            <PromptLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute requireSub>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Landing />} />
    </Routes>
    </>
  );
}

export default App;
